import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injectable,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { LocalStorageService } from '../../../common/services/local-storage.service';
import { ChangeStatusEnum } from '../../../core/enums';
import {
  FileTransferService,
  HintDialogService,
  UserProfileService,
} from '../../../core/services';
import { isNullOrEmpty } from '../../../core/utils';
import { UserProfile } from '../../../models/common';
import { DataTableComponent } from '../../../shared/components';
import {
  FileDisplayInfo,
  FileSyncData,
  FileUploadComponent,
} from '../../../shared/components/file-upload/file-upload.component';
import { FileSizePipe } from '../../../shared/pipes';
import { RetailCodeTableKey } from '../../enums';
import { LocalStorageEnum } from '../../enums/local-storage.enum';
import { RetailDropdownOptionsService } from '../../services';
import { FileService } from '../../services/file.service';

/**
 * 消金使用檔案上傳元件
 */
@Component({
  selector: 'sigv-case-file-upload',
  templateUrl: './case-file-upload.component.html',
  styleUrls: ['./case-file-upload.component.scss'],
  providers: [
    UserProfileService,
    FileTransferService,
    FileSizePipe,
    HintDialogService,
  ],
})
@Injectable()
export class CaseFileUploadComponent
  extends FileUploadComponent
  implements OnInit, OnChanges
{
  /** dataTable component */
  @ViewChild('dataTable')
  dataTable!: DataTableComponent;
  //dealer web使用時不能用userComposer pipeline
  @Input() usePipeline: boolean = true;
  @Input() userName: string = '';
  @Input() useInCase: boolean = false;
  @Input() codeTableCode: string = 'A070';
  @Input() documentTypeBySubKey: boolean = false;
  /**
   * 是否為FormControl，目前吃AttachmentViewModel的格式(預設為false)
   */
  @Input() isFormControlFormat: boolean = false;

  @Input() hintDialogInputService!: HintDialogService;
  @Input() index: number = 0;
  @Input() showDocumentType = true;
  @Output() bindingFormControlChange = new EventEmitter<any[]>();
  @Output() easySyncfunction = new EventEmitter<Function>();
  @Output() fileChange = new EventEmitter<any[]>();
  pageData = {
    selectLists: {
      documentTypeOptions: [] as SelectItem[],
      consumerDocTypeOptions1: [] as SelectItem[],
      consumerDocTypeOptions2: [] as SelectItem[],
    },
  };
  constructor(
    useinjector: Injector,
    userProfileService: UserProfileService,
    fileTransferService: FileTransferService,
    fileSizePipe: FileSizePipe,
    hintDialogService: HintDialogService,
    fb: FormBuilder,
    cd: ChangeDetectorRef,
    public retailDropdownOptionsService: RetailDropdownOptionsService,
    public _localStorageService: LocalStorageService,
    fileService: FileService
  ) {
    super(
      useinjector,
      userProfileService,
      fileTransferService,
      fileSizePipe,
      hintDialogService,
      fb,
      cd,
      _localStorageService,
      fileService
    );
  }

  util = {
    getSelectionOptionsBySubKey: (fileInfo: FileDisplayInfo) => {
      switch (fileInfo.subKey) {
        // SubKey = 1 抓A069
        // SubKey = 2 抓A069
        case 1:
        case 2:
          return this.pageData.selectLists.documentTypeOptions;
        // SubKey = 3 抓A257
        case 3:
        case 6: // #90677 Econsent的SubKey
          return this.pageData.selectLists.consumerDocTypeOptions2;
        // SubKey = 4 抓A069+A080
        case 4:
          return this.pageData.selectLists.documentTypeOptions.concat(
            this.pageData.selectLists.consumerDocTypeOptions1
          );
        default:
          return this.pageData.selectLists.documentTypeOptions;
      }
    },
    getAllSelectOptions: () => {
      if (this.documentTypeBySubKey) {
        const useCodeList = [
          RetailCodeTableKey.A069,
          RetailCodeTableKey.A080,
          RetailCodeTableKey.A257,
        ];
        const useSelectListKeysList = [
          'documentTypeOptions',
          'consumerDocTypeOptions1',
          'consumerDocTypeOptions2',
        ];
        this.retailDropdownOptionsService
          .batchGetAndSetDropdownOptionsPromise(
            useCodeList,
            this.pageData.selectLists,
            useSelectListKeysList
          )
          .then(() => {})
          .catch((error) => {
            console.error('batchGetAndSetDropdownOptionsPromise error:', error);
          });
      } else {
        this.retailDropdownOptionsService.getAndSetDropdownOptions(
          this.codeTableCode,
          this.pageData.selectLists,
          'documentTypeOptions'
        );
      }
    },
    convertToFormControl: () => {
      if (this.files.length > 0) {
        const convertToFormControls = [] as any[];
        this.files?.forEach((file) => {
          convertToFormControls.push({
            documentType: file.documentType,
            attachmentId: file.id,
            fileDetailId: file.fileId,
            fileDesc: file.description,
            createUserNo: file.creator,
            index: this.index,
            status: file.status,
          });
        });
        this.bindingFormControlChange.emit(convertToFormControls);
      }
    },
    convertToFileDisplayInfo: () => {
      if (this.files.length > 0) {
        const convertToFiles = [] as any[];
        this.files.forEach((element: any) => {
          convertToFiles.push({
            documentType: element.documentType,
            extension: element.extension,
            id: element.attachmentId,
            fileId: element.fileDetailId,
            fileSize: 0,
            status: ChangeStatusEnum.none,
            fileName: element.fileName ?? '',
            description: element.fileDesc,
            uploadTime: element.createTime,
            creator: element.createUserNo,
            editor: element.updateUserNo,
          });
        });
        this.files = convertToFiles;
      }
    },
    convertAttachmentToFileDisplayInfo: (
      dealerAttachments: AttachmentViewModel[]
    ) => {
      return (
        dealerAttachments?.map(
          (att) =>
            ({
              id: att.attachmentId ?? '',
              fileId: att.fileDetailId ?? '',
              status: ChangeStatusEnum.none,
              fileName: '',
              fileSize: 0,
              description: att.fileDesc ?? '',
              documentType: att.documentType ?? '',
              uploadTime: att.createTime,
              creator: att.createUserNo,
            } as FileDisplayInfo)
        ) ?? []
      );
    },
    /** 檢核是否有編輯中的資料 */
    validateTableEditing: (showErrorHint = false): boolean => {
      let checker = false;
      if (this.dataTable?.isTableEditing === true) {
        this.dataTable?.table.value.forEach((element) => {
          // 將編輯中的資料狀態押為 none，避免未完成編輯的資料上傳至 fileCenter
          if (element.editing == true) {
            element.status = ChangeStatusEnum.none;
          }
        });
        checker = true;
        if (showErrorHint) {
          this.hintDialogInputService.showErrorHint(
            'Library.UI.DataTable.CurrentlyEditing'
          );
        }
      }
      return checker;
    },
  };

  pageActionas = {
    callResult: (domain: string, subDomain: string): Promise<string[]> => {
      return new Promise((resolve) => {
        this.easySyncToFileCenter(domain, subDomain).subscribe({
          next: (result) => {
            resolve(result);
          },
          error: (err) => err,
        });
      });
    },
    /**
     * 同步至檔案中心(簡易版)
     *
     * @param {string} domain
     * @param {string} subDomain
     * @return {*}  {Observable<any>}
     * @memberof FileUploadComponent
     */
    easySyncToFileCenterWithActions: async (
      domain: string,
      subDomain: string,
      usefunc: any
    ): Promise<void> => {
      const syncData: FileSyncData = {
        domain,
        subDomain,
        newFiles: this.files.filter((x) => x.status === ChangeStatusEnum.added),
        updateFiles: this.files.filter(
          (x) => x.status === ChangeStatusEnum.modified
        ),
        deleteFiles: this.deletedFiles.filter((x) => !x.isTemp),
      };
      const fileIdList = await this.pageActionas.callResult(domain, subDomain);
      this.logger.debug('uploadfile-idlist', fileIdList);
      let addIndex = 0;
      this.files.forEach((element) => {
        if (element.status === ChangeStatusEnum.added) {
          this.logger.debug(
            'uploadfile-addfile',
            element,
            fileIdList[addIndex]
          );
          element.id = fileIdList[addIndex];
          element.fileId = fileIdList[addIndex];
          addIndex = addIndex + 1;
        }
      });
      this.logger.debug('uploadfile-usefunc', this.files);
      usefunc();
    },
    onRowEditValidate: (fileInfo: any, index: number): boolean => {
      if (!this.isFormDlw) {
        if (isNullOrEmpty(fileInfo?.fileName)) {
          this.hintDialogInputService?.showErrorHint(
            'Library.SupplementRecords.FileNameMustNotBeEmpty'
          );
          return false;
        }

        const fileNameList = this.files?.map((x) => x.fileName);
        const uniqueFileNames = new Set(fileNameList);
        if (
          this.isCheckDuplicateFileName &&
          uniqueFileNames.size !== fileNameList.length
        ) {
          this.hintDialogInputService?.showErrorHint(
            !isNullOrEmpty(this.fileNameDuplicateMsg)
              ? this.fileNameDuplicateMsg
              : 'Library.SupplementRecords.FileNameMustNotBeDuplicate'
          );
          return false;
        }
      }
      return true;
    },
  };

  ngOnInit() {
    this.fileColumns = this.useInCase
      ? [
          { field: 'fileId', header: 'IMG' },
          { field: 'documentType', header: 'File Type' },
          { field: 'fileName', header: 'File Name' },
          { field: 'fileSize', header: 'File Size' },
          { field: 'description', header: 'File Description', width: '300px' },
          { field: 'uploadTime', header: 'Upload Date' },
          {
            field: 'processor',
            header: 'Processor',
          },
        ]
      : [
          { field: 'fileId', header: 'IMG' },
          { field: 'fileName', header: 'File Name' },
          { field: 'fileSize', header: 'File Size' },
          { field: 'description', header: 'File Description' },
          { field: 'uploadTime', header: 'Upload Date' },
          {
            field: 'processor',
            header: 'Processor',
          },
        ];

    if (!this.showDocumentType) {
      this.fileColumns = this.useInCase
        ? [
            { field: 'fileId', header: 'IMG' },
            { field: 'fileName', header: 'File Name' },
            { field: 'fileSize', header: 'File Size' },
            {
              field: 'description',
              header: 'File Description',
              width: '300px',
            },
            { field: 'uploadTime', header: 'Upload Date' },
            {
              field: 'processor',
              header: 'Processor',
            },
          ]
        : [
            { field: 'fileId', header: 'IMG' },
            { field: 'fileName', header: 'File Name' },
            { field: 'fileSize', header: 'File Size' },
            { field: 'description', header: 'File Description' },
            { field: 'uploadTime', header: 'Upload Date' },
            {
              field: 'processor',
              header: 'Processor',
            },
          ];
    }
    this.easySyncfunction.emit(
      this.pageActionas.easySyncToFileCenterWithActions
    );
    this.util.getAllSelectOptions();
    if (this.isFormControlFormat) {
      this.util.convertToFileDisplayInfo();
    }
    this.userNo = (
      this._localStorageService.getItem(
        LocalStorageEnum.USER_INFO_STORAGE_KEY
      ) as UserProfile
    ).userNo;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.logger.debug('case file upload ngOnChanges', changes);
    let file = [] as any[];
    if (changes['files']) {
      for (let i = 0; i < changes['files'].currentValue.length; i++) {
        file.push({
          id: changes['files'].currentValue[i].id,
          fileName: changes['files'].currentValue[i].fileName,
          extension: changes['files'].currentValue[i].extension,
          description: changes['files'].currentValue[i].description,
          creator: changes['files'].currentValue[i].creator,
          documentType: changes['files'].currentValue[i].documentType,
        });
      }
      this.fileChange.emit(file);
      this.logger.debug('case file emit', file);

      this.easySyncfunction.emit(
        this.pageActionas.easySyncToFileCenterWithActions
      );
      if (this.isFormControlFormat) {
        if (changes['files']) {
          this.util.convertToFormControl();
        }
      }
    }
  }
}

export interface AttachmentViewModel {
  /**
   * 附件序號
   */
  attachmentId?: number;
  /**
   * 公司別
   */
  companyId?: number;
  /**
   * 附件歸屬案件序號  (例如：批覆書資料序號)
   */
  formId?: number;
  /**
   * 附件歸屬子表序號  (例如：擔保品資料序號)
   */
  subKey?: number;
  /**
   * 附件歸屬次子表序號  (例如：預留第3層)
   */
  secSubKey?: number;
  /**
   * 系統資料ID  與公司別一起當where條件，避免案件序號重覆無法索引正確的問題
   */
  systemFunctionId?: number;
  /**
   * 文件類型  引用代碼檔：A069/A070  (1) 若為”消費者新申請”或”消費者條件變更”，則顯示【代碼表A069】對應選項  (2) 若為”供應商新申請”或”供應商條件變更”，則顯示【代碼表A070】對應選項
   */
  documentType?: string;
  /**
   * 檔案上傳檔案中心後，會有一組File ID
   */
  fileDetailId?: string;
  /**
   * 檔案描述
   */
  fileDesc?: string;
  /**
   * 建立時間
   */
  createTime?: Date;
  /**
   * 建立人員員編
   */
  createUserNo?: string;
  /**
   * 更新時間
   */
  updateTime?: Date;
  /**
   * 更新人員員編
   */
  updateUserNo?: string;
}
